import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Home as HomeIcon, Ruler, ShieldCheck, Heart } from "lucide-react";
import { TrustScoreBadge } from "./TrustScoreWidget";
import { useSaveProperty } from "@/lib/useSaveProperty";

export default function PropertyCard({ item, index = 0 }) {
    const { saved, busy, toggle } = useSaveProperty(item.id);
    return (
        <Link
            to={`/properties/${item.slug || item.id}`}
            data-testid={`property-card-${item.slug || item.id}`}
            className="block group"
            style={{ animationDelay: `${(index % 6) * 80}ms` }}
        >
            <div className="relative property-image aspect-[4/5] bg-black/5 mb-5 border border-[var(--ebg-border-light)]">
                <img
                    src={item.cover_image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
                <button
                    type="button"
                    disabled={busy}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(); }}
                    data-testid={`property-fav-${item.slug || item.id}`}
                    aria-label={saved ? "Unsave" : "Save"}
                    className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${saved ? "bg-[var(--ebg-gold)] text-black" : "bg-white/90 text-black/60 hover:text-red-500"}`}
                >
                    <Heart size={14} fill={saved ? "currentColor" : "none"}/>
                </button>
                {item.trust_score?.total > 0 ? (
                    <div className="absolute top-3 right-3">
                        <TrustScoreBadge score={item.trust_score.total}/>
                    </div>
                ) : null}
                {item.legal_status === "verified" ? (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/70 backdrop-blur-md text-white px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold">
                        <ShieldCheck size={11} className="text-[var(--ebg-gold)]"/>
                        Legally Verified
                    </div>
                ) : null}
            </div>
            <div className="flex items-center gap-2 mb-3">
                <span className="overline text-[var(--ebg-gold)]">{item.status}</span>
                <span className="w-1 h-1 bg-[var(--ebg-gold)]" />
                <span className="overline text-black/50">{item.type}</span>
            </div>
            <h3 className="font-serif text-2xl md:text-[26px] leading-tight text-black">
                {item.title}
            </h3>
            <div className="flex items-center gap-2 mt-2 text-black/60 text-sm">
                <MapPin size={14} className="text-[var(--ebg-gold)]" />
                <span>{item.locality}, {item.city}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--ebg-border-light)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="font-serif text-base sm:text-lg text-black break-words">{item.price_display}</span>
                <div className="flex items-center gap-3 sm:gap-4 text-xs text-black/60 shrink-0">
                    {item.bhk ? (
                        <span className="flex items-center gap-1"><HomeIcon size={12}/> {item.bhk} BHK</span>
                    ) : null}
                    {item.area_sqft ? (
                        <span className="flex items-center gap-1"><Ruler size={12}/> {item.area_sqft.toLocaleString()} ft²</span>
                    ) : null}
                </div>
            </div>
        </Link>
    );
}
